import moment from "moment-timezone";
import * as _ from "lodash";

import { PendingCustomersResult } from "src/core/dashboard/api/widget-data.api";

import {
  DisplayRangeType,
  groupByDisplayRange
} from "../../utils";
import { PendingCustomersGraphValue } from "../PendingCustomersWidgetContent";

/**
 * Helper to group total tickets by date
 *
 * @param PendingCustomers A list of flat total tickets
 * @param xAxisBy: "date" | string where string is the name of the group by key
 * @param groupByKey optionsl group by key that comes from the widget type options
 * @param displayRange DisplayRangeType
 * @returns
 *
 * ### Example.
 *
 * **In:**
 * ```
 * const PendingCustomers = [
 *  {
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "GroupByName": "Value"
    },
    {
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "GroupByName": "Value"
    },
    {
      "date": "Thu Feb 03 2022 00:00:00",
      "count": 1,
      "GroupByName": "Value"
    }
 * ]
 * const displayRange = "month"
 * ```
 * **Out:**
 * ```
 *[
    {
      "date": "2022-01-01T00:00:00.000Z",
      "value": 2
    },
    OR
    {
      "GROUP_BY_KEY": "VALUE",
      "value": 1
    }
  ]
 * ```
 */
export const groupTotalPendingCustomers = (pendingCustomerResult: PendingCustomersResult, xAxisBy: "date" | string, groupByKey: string, displayRange?: DisplayRangeType): PendingCustomersGraphValue[] => {
  const result: PendingCustomersGraphValue[] = [];
  // Group the data based on xAxisBy value
  const groupedCustomerData = xAxisBy === "date" && displayRange ? groupByDisplayRange(pendingCustomerResult, displayRange) : _.groupBy(pendingCustomerResult, groupByKey);

  // Loop grouped data to form the result, ensuring to include xAxisBy property and all relevant values accumulated
  for (const xAxisGroup in groupedCustomerData) {
    if (xAxisBy === "date") {
      // accumulative totals for xAxisBy date

      let dataItem: PendingCustomersGraphValue;

      if (groupByKey !== "date") {
        const accumulativeTotals = {};

        groupedCustomerData[ xAxisGroup ].forEach(row => {
          if (accumulativeTotals[ row[ groupByKey ] ]) {
            accumulativeTotals[ row[ groupByKey ] ] += row.count;
          } else {
            accumulativeTotals[ row[ groupByKey ] ] = row.count;
          }
        });

        dataItem = {
          date: moment.utc(xAxisGroup).toDate(),
          // Only include groups that have values
          ..._.pickBy(accumulativeTotals, value => value !== 0)
        };
      } else {
        const accumulativeDateTotals = { value: 0 };

        groupedCustomerData[ xAxisGroup ].forEach(row => {
          accumulativeDateTotals.value += row.count;
        });

        dataItem = {
          date: moment.utc(xAxisGroup).toDate(),
          ...accumulativeDateTotals
        };
      }

      // Force header to include date day if displayRange not by month
      if (displayRange !== "month") {
        dataItem.toolTipHeader = moment.utc(xAxisGroup).format("Do MMMM YYYY");
      }

      result.push(dataItem);
    } else {
      // accumulative total for xAxisBy groupByKey
      let accumulativeTotal = 0;

      groupedCustomerData[ xAxisGroup ].forEach(row => {
        accumulativeTotal += row.count;
      });

      const dataItem = {
        [ groupByKey ]: xAxisGroup,
        value: accumulativeTotal,
        toolTipHeader: _.startCase(xAxisGroup.replace(/[_-]/g, " "))
      };

      result.push(dataItem);
    }
  }

  return result;
};
